<template>
  <Transition :name="transitionName" mode="out-in">
    <div class="widget-step-selector">
      <h2
        v-if="!isBackButtonVisible"
        class="title3-bold widget-step-selector_title mb-4 font-medium text-primary-500"
        :class="componentStyles?.title || ''"
      >
        {{ title }}
      </h2>
      <div v-else>
        <WButton
          type="ghost"
          class="widget-step-selector_back-btn group !-ml-2 mb-4 hover:border-transparent hover:bg-white"
          :class="componentStyles?.button?.back"
          @click="onBack"
        >
          <WidgetIcon :icon="themeIcons.chevronLeft" class="widget-primary-icon size-6" />
          <h2 class="widget-form-renderer_title group-hover:underline">{{ title || 'BACK' }}</h2>
        </WButton>
      </div>
      <p v-if="description" class="body2 mb-9 text-fontSecondary-500">
        {{ description }}
      </p>
      <ul role="list" :class="twMerge(selectorVariants({ variant }))">
        <li v-for="(card, index) in cards" :key="card.name" class="flex">
          <WButton
            type="ghost"
            :class="[twMerge(itemVariants({ variant })), componentStyles?.button?.step || '']"
            @click="onSelect(index)"
          >
            <div class="flex items-center">
              <div class="mr-4 flex h-12 w-12 items-center justify-center rounded-full">
                <WidgetIcon :icon="card.icon" class="widget-primary-icon size-12" />
              </div>
              <div v-if="checkTheme('landrover')" class="-ml-1 mr-2 flex items-center justify-center rounded-full">
                <WidgetIcon :icon="themeIcons.chevronRight" class="widget-primary-icon size-6" />
              </div>
              <div>
                <h2 class="widget-step-selector_title" :class="componentStyles?.title || ''">
                  {{ card.name }}
                </h2>
                <p class="widget-step-selector_description" :class="componentStyles?.body || ''">
                  {{ card.description }}
                </p>
              </div>
            </div>
            <div
              v-if="
                checkTheme(
                  'volvo',
                  'tesla',
                  'aa',
                  'cinch',
                  'crashdaddy',
                  'jaguar',
                  'jlr',
                  'referral',
                  'bmw',
                  'toyota',
                  'samsung'
                )
              "
              class="flex h-12 items-center justify-center rounded-full"
            >
              <WidgetIcon :icon="themeIcons.chevronRight" class="widget-primary-icon size-6" />
            </div>
          </WButton>
        </li>
      </ul>
    </div>
  </Transition>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { cva, type VariantProps } from 'class-variance-authority';
  import { twMerge } from 'tailwind-merge';
  import { themeIcons, checkTheme, getClientTheme, getComponentStyle } from 'src/utils/config';
  import { sendAnalytics } from 'src/utils/analytics';
  import WButton from '../../base/Button.vue';
  import WidgetIcon from 'src/components/base/WidgetIcon.vue';

  const componentStyles = getComponentStyle();

  const selectorVariants = cva('flex flex-col gap-y-4 rounded-custom', {
    variants: {
      variant: {
        default: '',
        audi: '',
        samsung: '',
        volkswagen: '',
        bentley: '',
        vwg: '',
        mercedes: '',
        bmw: '',
        toyota: '',
        mopar: '',
        jeep: '',
        mini: '',
        jaguar: '',
        jlr: '',
        common: '',
        referral: '',
        landrover: '',
        volvo: 'gap-y-0 border border-backgroundSoft-600 p-2',
        tesla: 'gap-y-0 border border-backgroundSoft-600 p-2',
        aa: 'gap-y-0 border border-backgroundSoft-600 p-2',
        suzuki: '',
        cinch: 'gap-y-0 border border-backgroundSoft-600 p-2',
        crashdaddy: 'gap-y-0 border border-backgroundSoft-600 p-2',
      },
    },
    defaultVariants: {
      variant: getClientTheme(),
    },
  });

  const itemVariants = cva('items-start w-full p-4 border border-backgroundSoft-600 ', {
    variants: {
      variant: {
        default: '',
        audi: '',
        volkswagen: '',
        bentley: '',
        vwg: '',
        mercedes: '',
        bmw: 'justify-between',
        toyota: 'justify-between',
        mopar: 'justify-between',
        jeep: 'justify-between',
        samsung: 'justify-between rounded-[0.8rem] border-0 shadow-[1px_1px_20px_2px_rgba(147,152,159,0.2)]',
        mini: 'justify-between',
        jaguar: 'justify-between',
        jlr: 'justify-between',
        common: 'justify-between',
        referral: 'justify-between',
        landrover: '',
        volvo: 'py-3 px-4 justify-between border-0',
        tesla: 'py-3 px-4 justify-between border-0',
        aa: 'py-3 px-4 justify-between border-0',
        suzuki: 'py-3 px-4 justify-between border border-primary bg-white hover:bg-backgroundSoft-400',
        cinch: 'py-3 px-4 justify-between border-0',
        crashdaddy: 'py-3 px-4 justify-between border-0',
      },
    },
    defaultVariants: {
      variant: getClientTheme(),
    },
  });

  type SelectorVariants = VariantProps<typeof selectorVariants>;

  const props = defineProps<{
    title: string;
    description: string;
    cards: {
      name: string;
      description: string;
      icon: string;
    }[];
    isBackButtonVisible: boolean;
    variant?: SelectorVariants['variant'];
  }>();
  const emit = defineEmits<{
    (event: 'onSelect', index: number): void;
    (event: 'back'): void;
  }>();

  function onSelect(index: number) {
    sendAnalytics('event', 'step_change', {
      event_category: 'STEP',
      event_label: 'STEP_SELECTED',
      to: props.cards[index]?.name,
      send_to: env.GA_ID,
    });
    emit('onSelect', index);
  }
  function onBack() {
    sendAnalytics('event', 'step_change', {
      event_category: 'STEP',
      event_label: 'BACK',
      from: props.title,
      send_to: env.GA_ID,
    });
    emit('back');
  }
  const transitionName = computed<'slide-left' | 'slide-right'>(() =>
    props.isBackButtonVisible ? 'slide-left' : 'slide-right'
  );
</script>
